'use client';

import Link from 'next/link';
import Image from 'next/image';
import { BlogPost } from '@prisma/client';
import { CategoryBadge, getCategoryColors } from './CategoryBadge';
import { Clock, Calendar, ArrowRight, Sparkles } from 'lucide-react';

interface BlogCardProps {
  post: BlogPost;
  featured?: boolean;
  customHref?: string; // Allow parent to override the URL
}

export function BlogCard({ post, featured = false, customHref }: BlogCardProps) {
  const colors = getCategoryColors(post.category);
  const categoryPath = post.category.toLowerCase();

  const formattedDate = post.publishedAt
    ? new Date(post.publishedAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '';

  // If customHref is provided, use it (for multi-language pages)
  // Otherwise, support both old and new URL formats for backwards compatibility
  const postUrl = customHref || (post.slug.startsWith('/') ? post.slug : `/blogs/${categoryPath}/${post.slug}`);

  return (
    <Link href={postUrl} className="blog-card-link">
      <article
        className={`blog-card ${featured ? 'featured' : ''}`}
        style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: featured ? '32px' : '24px',
          overflow: 'hidden',
          border: '2px solid rgba(255, 255, 255, 0.8)',
          boxShadow: '0 10px 40px -10px rgba(0, 0, 0, 0.08)',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          cursor: 'pointer',
          position: 'relative',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
          e.currentTarget.style.boxShadow = `0 20px 60px -15px ${colors.border}60`;
          e.currentTarget.style.borderColor = colors.border;
          const overlay = e.currentTarget.querySelector('.card-overlay') as HTMLElement;
          if (overlay) overlay.style.opacity = '0.15';
          const arrow = e.currentTarget.querySelector('.read-more-arrow') as HTMLElement;
          if (arrow) {
            arrow.style.opacity = '1';
            arrow.style.transform = 'translateX(5px)';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0) scale(1)';
          e.currentTarget.style.boxShadow = '0 10px 40px -10px rgba(0, 0, 0, 0.08)';
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.8)';
          const overlay = e.currentTarget.querySelector('.card-overlay') as HTMLElement;
          if (overlay) overlay.style.opacity = '0';
          const arrow = e.currentTarget.querySelector('.read-more-arrow') as HTMLElement;
          if (arrow) {
            arrow.style.opacity = '0';
            arrow.style.transform = 'translateX(-5px)';
          }
        }}
      >
        {/* Gradient Overlay */}
        <div
          className="card-overlay"
          style={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(135deg, ${colors.border}20 0%, ${colors.bg}30 100%)`,
            opacity: 0,
            transition: 'opacity 0.4s ease',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        {/* Cover Image */}
        {post.coverImage && (
          <div
            style={{
              position: 'relative',
              width: '100%',
              height: featured ? '320px' : '240px',
              backgroundColor: colors.bg,
              overflow: 'hidden',
            }}
          >
            <Image
              src={post.coverImage}
              alt={post.title}
              fill
              style={{ objectFit: 'cover', transition: 'transform 0.4s ease' }}
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            />
            {/* Premium Badge */}
            <div style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              padding: '0.5rem 1rem',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: '50px',
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
              fontSize: '0.75rem',
              fontWeight: 700,
              color: colors.text,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            }}>
              <Sparkles size={12} fill={colors.text} />
              <span>Premium</span>
            </div>
          </div>
        )}

        {/* Content */}
        <div style={{ padding: featured ? '2rem' : '1.75rem', flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 1 }}>
          {/* Category Badge */}
          <div style={{ marginBottom: '1rem' }}>
            <CategoryBadge category={post.category} size="sm" />
          </div>

          {/* Title */}
          <h3
            style={{
              fontSize: featured ? '1.625rem' : '1.375rem',
              fontWeight: 800,
              marginBottom: '0.75rem',
              lineHeight: 1.3,
              color: '#1a1a1a',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              letterSpacing: '-0.01em',
            }}
          >
            {post.title}
          </h3>

          {/* Summary */}
          <p
            style={{
              fontSize: '1rem',
              color: '#64748b',
              lineHeight: 1.7,
              marginBottom: '1.25rem',
              flex: 1,
              display: '-webkit-box',
              WebkitLineClamp: featured ? 3 : 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              fontWeight: 500,
            }}
          >
            {post.summary}
          </p>

          {/* Meta Information */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem',
              paddingTop: '1rem',
              borderTop: '2px solid rgba(0, 0, 0, 0.05)',
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              fontSize: '0.875rem',
              color: '#94a3b8',
              fontWeight: 600,
            }}>
              {formattedDate && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                  <Calendar size={14} />
                  <span>{formattedDate}</span>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <Clock size={14} />
                <span>{post.readingTime} min</span>
              </div>
              {post.ageRange && (
                <span
                  style={{
                    padding: '0.375rem 0.75rem',
                    borderRadius: '12px',
                    background: colors.bg,
                    color: colors.text,
                    fontSize: '0.8125rem',
                    fontWeight: 700,
                  }}
                >
                  {post.ageRange}
                </span>
              )}
            </div>
            <ArrowRight 
              className="read-more-arrow"
              size={20} 
              style={{ 
                color: colors.text,
                opacity: 0,
                transition: 'all 0.3s ease',
                transform: 'translateX(-5px)',
              }} 
            />
          </div>
        </div>
      </article>

      <style jsx>{`
        .blog-card-link {
          display: block;
          text-decoration: none;
          height: 100%;
        }

        @media (max-width: 768px) {
          .blog-card h3 {
            font-size: 1.125rem !important;
          }
          .blog-card p {
            font-size: 0.9375rem !important;
          }
        }
      `}</style>
    </Link>
  );
}

